import React, { useState, useEffect } from 'react';
import { useGetAiWorkerUsageByIdMutation, useGetAiWorkerByIdQuery, useTypedSelector } from '@circuitry-ai/doc-data';
import { Icon } from '@iconify/react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FormGenieWorker, FormConfig } from './types';
import { parseFormConfig } from './utils';
import FormGenieHeader from './components/FormGenieHeader';
import FormGenieSplitPanel from './components/FormGenieSplitPanel';

const FormGenie: React.FC<{ aiworkerId?: string; basePath?: string }> = ({
  aiworkerId: propAiworkerId,
  basePath: propBasePath,
}) => {
  const basePath = propBasePath ?? '/formgenie';
  const location = useLocation();
  const navigate = useNavigate();
  const { aiworker_id: routeAiworkerId, usage_id: routeUsageId } = useParams<{
    aiworker_id?: string;
    usage_id?: string;
  }>();

  const effectiveAiworkerId = propAiworkerId || routeAiworkerId;
  const navState = location.state as any;
  const token = useTypedSelector((state: any) => state.auth.token) as string | null;

  const [formGenieWorker, setFormGenieWorker] = useState<FormGenieWorker | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [initialFormConfig, setInitialFormConfig] = useState<FormConfig | undefined>();
  const [initialSchemaInput, setInitialSchemaInput] = useState<string | undefined>();
  const [currentFormName, setCurrentFormName] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);
  const [isViewingForm, setIsViewingForm] = useState(false);

  const [getRecordById] = useGetAiWorkerUsageByIdMutation();

  const { data: fetchedWorkerData } = useGetAiWorkerByIdQuery(effectiveAiworkerId || '', {
    skip: !effectiveAiworkerId || !token,
  });

  useEffect(() => {
    if (fetchedWorkerData && !formGenieWorker) {
      const workerData = (fetchedWorkerData as any)?.data ?? fetchedWorkerData;
      setFormGenieWorker(workerData);
    }
  }, [fetchedWorkerData, formGenieWorker]);

  useEffect(() => {
    if (navState?.formGenieWorker) setFormGenieWorker(navState.formGenieWorker);
    if ((navState as any)?.readOnly) setIsReadOnlyMode(true);

    const isEdit = location.pathname.includes('/edit/') || (navState as any)?.editMode === true;
    if (isEdit) setIsEditMode(true);
    if (isEdit && routeUsageId) return;

    if (navState?.record) extractAndSetRecord(navState.record, isEdit);
  }, [navState, location.pathname, routeUsageId]);

  useEffect(() => {
    const isEditModeFromUrl = location.pathname.includes('/edit/');
    if (!isEditModeFromUrl || !routeUsageId) return;

    setIsEditMode(true);
    setIsLoadingRecord(true);

    getRecordById(routeUsageId)
      .unwrap()
      .then((response) => {
        let record = response;
        if (Array.isArray(response) && response.length > 0) record = response[0];
        else if (response?.data && Array.isArray(response.data)) record = response.data[0];
        else if (response?.data && !Array.isArray(response.data)) record = response.data;
        extractAndSetRecord(record, true);
      })
      .catch((err) => console.error('Failed to fetch form record:', err))
      .finally(() => setIsLoadingRecord(false));
  }, [routeUsageId, location.pathname]);

  const extractAndSetRecord = (record: any, _isEdit: boolean) => {
    const outputData = record.output_data || record.action_output;
    // Support both old (form_config) and current (generated_code) field names
    const rawCode = outputData?.generated_code || outputData?.form_config;
    if (!rawCode) return;

    const config = parseFormConfig(rawCode);
    if (config) setInitialFormConfig(config);

    // Support both old (schema_input) and current (original_data) field names
    const schemaInput = record.input_data?.schema_input || record.input_data?.original_data || '';
    if (schemaInput) setInitialSchemaInput(schemaInput);

    setCurrentFormName(outputData?.action_name || outputData?.form_name || config?.formName || null);
    setIsViewingForm(true);
  };

  const handleBack = () => {
    const workerId = routeAiworkerId || formGenieWorker?.aiworker_id;
    if (workerId) navigate(`${basePath}/${workerId}?tab=forms`);
  };

  if (!token) {
    return (
      <div className="flex items-center justify-center h-full text-default-500">
        <p>Please log in to use FormGenie.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-content1 relative overflow-hidden">
      <FormGenieHeader
        formGenieWorker={formGenieWorker}
        isViewingForm={isViewingForm}
        hasGeneratedForm={isEditMode || !!initialFormConfig}
        currentFormName={currentFormName}
        onBack={handleBack}
      />

      {isLoadingRecord ? (
        <div className="flex items-center justify-center flex-1 text-blue-600">
          <Icon icon="eos-icons:loading" width={24} />
          <span className="ml-2">Loading form...</span>
        </div>
      ) : (
        <FormGenieSplitPanel
          isEdit={isEditMode}
          isReadOnly={isReadOnlyMode}
          initialFormConfig={initialFormConfig}
          initialSchemaInput={initialSchemaInput}
          initialFormName={currentFormName}
          initialUsageId={routeUsageId}
          token={token}
          worker={formGenieWorker}
          onSaveComplete={(workerId) => navigate(`${basePath}/${workerId}?tab=forms`)}
        />
      )}
    </div>
  );
};

export default FormGenie;
